import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import './BillingDashboard.css';

interface PaymentMethod {
  id: string;
  type: 'card' | 'bank_account';
  last4?: string;
  brand?: string;
  expiry_month?: number;
  expiry_year?: number;
  is_default: boolean;
}

interface PaymentMethodSelectorProps {
  onSelect?: (methodId: string) => void;
  onAddNew?: () => void;
  selectedMethodId?: string;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  onSelect,
  onAddNew,
  selectedMethodId
}) => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: Implement API endpoint for fetching saved payment methods
      // For now, this is a placeholder
      // const token = localStorage.getItem('token');
      // const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // const response = await axios.get(`${config.apiUrl}/api/payment-methods`, { headers });
      // setPaymentMethods(response.data || []);
      
      // Placeholder data
      setPaymentMethods([]);
    } catch (err: any) {
      console.error('Error fetching payment methods:', err);
      setError(err.response?.data?.detail || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (methodId: string) => {
    if (onSelect) {
      onSelect(methodId);
    }
  };

  const handleSetDefault = async (methodId: string) => {
    try {
      // TODO: Implement API endpoint for setting default payment method
      // const token = localStorage.getItem('token');
      // const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // await axios.put(`${config.apiUrl}/api/payment-methods/${methodId}/set-default`, {}, { headers });
      // fetchPaymentMethods();
    } catch (err: any) {
      console.error('Error setting default payment method:', err);
    }
  };

  const handleRemove = async (methodId: string) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) {
      return;
    }
    
    try {
      // TODO: Implement API endpoint for removing payment method
      // const token = localStorage.getItem('token');
      // const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // await axios.delete(`${config.apiUrl}/api/payment-methods/${methodId}`, { headers });
      // fetchPaymentMethods();
    } catch (err: any) {
      console.error('Error removing payment method:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading payment methods...</div>;
  }

  return (
    <div className="payment-method-selector">
      <div className="payment-method-selector-header">
        <h3>Payment Methods</h3>
        {onAddNew && (
          <button className="btn btn-primary btn-sm" onClick={onAddNew}>
            Add New Payment Method
          </button>
        )}
      </div>
      
      {error && (
        <div className="error-message">{error}</div>
      )}
      
      {paymentMethods.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '60px 20px', backgroundColor: '#f8f9fa', borderRadius: '12px', border: '2px dashed #dee2e6' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚧</div>
          <h3 style={{ color: '#495057', marginBottom: '10px' }}>Feature Currently Not Available</h3>
          <p style={{ color: '#6c757d', fontSize: '16px', maxWidth: '500px', margin: '0 auto' }}>
            The ability to save and manage payment methods is currently under development and will be available soon.
          </p>
          <p style={{ color: '#6c757d', fontSize: '14px', marginTop: '15px' }}>
            For now, you can enter your payment details each time you make a payment.
          </p>
        </div>
      ) : (
        <div className="payment-methods-list">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className={`payment-method-item ${selectedMethodId === method.id ? 'selected' : ''}`}
              onClick={() => handleSelect(method.id)}
            >
              <div className="payment-method-info">
                {method.type === 'card' && (
                  <>
                    <div className="payment-method-icon">💳</div>
                    <div className="payment-method-details">
                      <div className="payment-method-name">
                        {method.brand || 'Card'} •••• {method.last4}
                      </div>
                      {method.expiry_month && method.expiry_year && (
                        <div className="payment-method-expiry">
                          Expires {method.expiry_month}/{method.expiry_year}
                        </div>
                      )}
                    </div>
                  </>
                )}
                {method.type === 'bank_account' && (
                  <>
                    <div className="payment-method-icon">🏦</div>
                    <div className="payment-method-details">
                      <div className="payment-method-name">
                        Bank Account •••• {method.last4}
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <div className="payment-method-actions">
                {method.is_default && (
                  <span className="default-badge">Default</span>
                )}
                {!method.is_default && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetDefault(method.id);
                    }}
                  >
                    Set Default
                  </button>
                )}
                <button
                  className="btn btn-danger btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(method.id);
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentMethodSelector;




